import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createPagingApiResponseSchema } from '../utils';
import { curationsApiPath } from './apiPath';

/** 스냅 항목 — `goods` 누락 시 빈 배열로 통일 */
const withDefaultGoods = <T extends { goods?: unknown }>(item: T) => ({
  ...item,
  goods: Array.isArray(item.goods) ? item.goods : [],
});

/** GET `/v2/curations/snaps` — SnapGoodsResponse (OpenAPI Curation V2) */
const SnapGoodsV2Schema = z
  .object({
    goodsNo: z.union([z.string(), z.number()]),
    goodsName: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    brandLinkUrl: z.string(),
    linkUrl: z.string(),
    imageUrl: z.string(),
    normalPrice: z.number().int(),
    price: z.number().int(),
    couponPrice: z.number().int().nullish(),
    couponSalesRate: z.number().int().nullish(),
    extraDiscountAmount: z
      .number()
      .int()
      .nullish()
      .describe('추가할인 금액(원). 표시는 finalPrice 사용 (주문 도메인 전용, MUSINSA 전용)'),
    finalPrice: z
      .number()
      .int()
      .nullish()
      .describe('최종가 = 세일가 + 상품쿠폰 + 추가할인 (장바구니쿠폰 제외, MUSINSA 전용)'),
    finalDiscount: z
      .number()
      .int()
      .nullish()
      .describe('정상가 대비 finalPrice 할인율 (MUSINSA 전용)'),
    isLowestPrice: z
      .boolean()
      .nullish()
      .describe('RADAR 추가할인 최저가 보장 여부 — true 시 최저가도전 라벨 (MUSINSA 전용)'),
    isExclusive: z.boolean().nullish().describe('단독 상품 여부 (MUSINSA 전용)'),
    salesRate: z.number().int(),
    isSoldOut: z.boolean(),
    isLiked: z.boolean().nullish(),
    hasOptionPrice: z.boolean().nullish(),
  })
  .describe('SnapGoodsV2Schema');

/** GET `/v2/curations/snaps` — GetMySnapsV2Response */
const GetMySnapsV2ItemSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    imageUrl: z.string().nullish(),
    /** 일부 응답에서 다중 이미지로 내려올 수 있음 */
    imageUrls: z.array(z.string()).optional(),
    linkUrl: z.string(),
    goods: z.array(SnapGoodsV2Schema).nullish(),
  })
  .transform(withDefaultGoods)
  .describe('GetMySnapsV2ItemSchema');

const getMySnapsListPayloadSchema = createPagingApiResponseSchema(GetMySnapsV2ItemSchema);

/**
 * 실제 응답이 OpenAPI `{ list }` 이거나, 무신사 표준 `{ data: { list }, meta, link }` 인 경우 모두 수용.
 * 서비스 레이어는 항상 `{ list }` 만 받도록 정규화합니다.
 */
export const GetMySnapsResponseSchema = z
  .preprocess((val: unknown): unknown => {
    if (!val || typeof val !== 'object') {
      return val;
    }
    const body = val as Record<string, unknown>;
    if ('list' in body && Array.isArray(body.list)) {
      return { list: body.list };
    }
    const data = body.data;
    if (data && typeof data === 'object' && 'list' in data) {
      const dataObj = data as Record<string, unknown>;
      if (Array.isArray(dataObj.list)) {
        return { list: dataObj.list };
      }
    }
    return val;
  }, getMySnapsListPayloadSchema)
  .describe('GetMySnapsResponseSchema');

export type GetMySnapsResponse = z.infer<typeof GetMySnapsResponseSchema>;

const GetMySnapsRequestSchema = z
  .object({
    page: z.number().int().min(1),
    size: z.number().int().min(1).max(100),
  })
  .describe('GetMySnapsRequestSchema');

export type GetMySnapsRequest = z.infer<typeof GetMySnapsRequestSchema>;

export const getMySnaps = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetMySnapsRequestSchema),
    async (params: GetMySnapsRequest): Promise<GetMySnapsResponse> =>
      executeStandardRequest(async () => {
        const { page, size } = params;
        const query: Record<string, number> = { page, size };
        return privateHttpClient.get<GetMySnapsResponse>(curationsApiPath.getMySnaps, query);
      }),
    validateApiResponse(GetMySnapsResponseSchema),
  ),
);
